import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Dropdown, Input, Checkbox, FroalaTextEditor } from 'components/UIElements'
import { stripHtmlTags } from 'utils/misc'
import STRINGS from 'utils/strings'
import { itemActions } from '../modules/Survey'

const DEVICE_TYPE_OPTIONS = [
  { key: 'mir_smart', value: 'mir_smart', text: 'MIR Smart' },
  { key: 'roche_accu_chek', value: 'roche_accu_chek', text: 'Roche Accu-Chek' },
]

const MIR_SMART_TEST_TYPE_OPTIONS = [{ key: 'PEFFEV1', value: 'PEFFEV1', text: 'PEFFEV1' }]

const MIR_SMART_MIN_STATUS_OPTIONS = [
  { key: 4, value: 4, text: 'Acceptable' },
  { key: 3, value: 3, text: 'Not Acceptable But Usable' },
  { key: 2, value: 2, text: 'Not Acceptable' },
  { key: 1, value: 1, text: 'Not Acceptable And Not Usable' },
  { key: 0, value: 0, text: 'Not Applicable' },
]

const FROALA_RICH_BUTTONS = ['insertLink', 'insertTable', 'emoticons', 'specialCharacters']

const ACCU_CHEK_FIELD_KEYS = [
  'has_on_demand_question_text',
  'on_demand_question_text',
  'has_prefilled_question_text',
  'prefilled_question_text',
]

const MIR_SMART_FIELD_KEYS = ['test_type', 'min_status_required', 'max_blows_allowed', 'target_good_blows']

const IntegratedDevice = props => {
  const { item, updateItem, itemId, errors, clearQuestionError, preview } = props
  const integratedDeviceFields = item?.attributes?.integrated_device_fields || {}
  const isMirSmart = integratedDeviceFields.device_type === 'mir_smart'
  const isAccuChek = integratedDeviceFields.device_type === 'roche_accu_chek'
  const hasOnDemandQuestionText = !!integratedDeviceFields.has_on_demand_question_text
  const hasPrefilledQuestionText = !!integratedDeviceFields.has_prefilled_question_text
  const editorReadOnly = preview

  // Froala blur handlers can fire with a stale onChange closure. Keep the latest item
  // in a ref so Accu-Chek text edits never overwrite sibling toggle flags.
  const itemRef = useRef(item)
  useEffect(() => {
    itemRef.current = item
  }, [item])

  const getSelectedKey = type => {
    const val = integratedDeviceFields[type]
    return val !== undefined && val !== null ? val : ''
  }

  const getCurrentIntegratedDeviceFields = () =>
    itemRef.current?.attributes?.integrated_device_fields || {}

  const updateIntegratedDeviceFields = updatedFields => {
    const currentItem = itemRef.current || item
    // Only patch attributes so a stale full `item` snapshot cannot wipe other question fields.
    updateItem(itemId, {
      attributes: {
        ...currentItem.attributes,
        integrated_device_fields: updatedFields,
      },
    })
    clearQuestionError(itemId)
  }

  const clearAccuChekFields = fields => {
    ACCU_CHEK_FIELD_KEYS.forEach(key => {
      delete fields[key]
    })
    return fields
  }

  const clearMirSmartFields = fields => {
    MIR_SMART_FIELD_KEYS.forEach(key => {
      delete fields[key]
    })
    return fields
  }

  const updateDeviceTypeField = (type, key) => {
    let updatedFields = {
      ...getCurrentIntegratedDeviceFields(),
      [type]: key,
    }

    if (type === 'device_type') {
      if (key === 'mir_smart') {
        updatedFields = clearAccuChekFields(updatedFields)
        updatedFields = {
          ...updatedFields,
          test_type: updatedFields.test_type || 'PEFFEV1',
          min_status_required: updatedFields.min_status_required !== undefined ? updatedFields.min_status_required : 4,
          max_blows_allowed: updatedFields.max_blows_allowed || 5,
          target_good_blows: updatedFields.target_good_blows || 3,
        }
      } else if (key === 'roche_accu_chek') {
        updatedFields = clearMirSmartFields(updatedFields)
      } else {
        updatedFields = clearMirSmartFields(clearAccuChekFields(updatedFields))
      }
    }

    updateIntegratedDeviceFields(updatedFields)
  }

  const updateNumericField = (field, value) => {
    const numValue = value === '' ? '' : parseInt(value, 10)
    updateIntegratedDeviceFields({
      ...getCurrentIntegratedDeviceFields(),
      [field]: numValue,
    })
  }

  const updateQuestionTextToggle = (toggleField, textField) => {
    const currentFields = getCurrentIntegratedDeviceFields()
    const enabled = !currentFields[toggleField]
    updateIntegratedDeviceFields({
      ...currentFields,
      [toggleField]: enabled,
      ...(!enabled ? { [textField]: '' } : {}),
    })
  }

  const updateQuestionText = (textField, newHtml) => {
    updateIntegratedDeviceFields({
      ...getCurrentIntegratedDeviceFields(),
      [textField]: newHtml,
    })
  }

  const renderQuestionTextSection = ({
    toggleField,
    textField,
    toggleLabel,
    placeholder,
    isEnabled,
    errorKey,
  }) => (
    <div className='accu-chek-question-text attribute panel'>
      <Checkbox
        checked={isEnabled}
        onClick={() => updateQuestionTextToggle(toggleField, textField)}
        label={toggleLabel}
        toggle
        disabled={editorReadOnly}
      />
      {isEnabled ? (
        <div className='accu-chek-question-text-editor'>
          <FroalaTextEditor
            value={integratedDeviceFields[textField] || ''}
            onChange={newHtml => updateQuestionText(textField, newHtml)}
            hasBlockStyles
            hasError={!!errors?.[errorKey] && !stripHtmlTags(integratedDeviceFields[textField] || '').trim()}
            readOnly={editorReadOnly}
            spellCheck
            moreText={{
              includeAll: true,
            }}
            moreParagraph={{
              includeAll: true,
            }}
            moreRich={{
              buttons: FROALA_RICH_BUTTONS,
              includeAll: false,
            }}
            placeholder={placeholder}
            {...props}
          />
        </div>
      ) : null}
    </div>
  )

  return (
    <div className='integrated-device'>
      <div className='mir-smart-row-1'>
        <div className='mir-smart-field-half'>
          <div className='device-type-subtitle'>Device Type</div>
          <div className={`device-type ${errors?.blankIntegratedDeviceField ? 'error' : ''}`}>
            <Dropdown
              className='select-dropdown'
              id='select-device-type'
              disabled={preview}
              onSelect={field => updateDeviceTypeField('device_type', field.key)}
              options={DEVICE_TYPE_OPTIONS}
              selected={getSelectedKey('device_type')}
              sortingOrder='asc'
              sortingBy='text'
            />
          </div>
        </div>
        {isMirSmart && (
          <div className='mir-smart-field-half'>
            <div className='device-type-subtitle'>Test Type</div>
            <Dropdown
              className='select-dropdown'
              id='select-test-type'
              disabled={preview}
              onSelect={field => updateDeviceTypeField('test_type', field.key)}
              options={MIR_SMART_TEST_TYPE_OPTIONS}
              selected={getSelectedKey('test_type')}
            />
          </div>
        )}
      </div>
      {isMirSmart && (
        <div className='mir-smart-row-2'>
          <div className='mir-smart-field-min-status'>
            <div className='device-type-subtitle'>Minimum Status Required for Good Blow</div>
            <Dropdown
              className='select-dropdown'
              id='select-min-status'
              disabled={preview}
              onSelect={field => updateDeviceTypeField('min_status_required', field.key)}
              options={MIR_SMART_MIN_STATUS_OPTIONS}
              selected={getSelectedKey('min_status_required')}
            />
          </div>
          <div className='mir-smart-field-numeric'>
            <div className='device-type-subtitle'>Max Blows Allowed</div>
            <Input
              type='number'
              min={1}
              max={100}
              enforceMinMax
              onlyInteger
              isPositive
              disabled={preview}
              value={getSelectedKey('max_blows_allowed')}
              onChange={val => updateNumericField('max_blows_allowed', val)}
              hasError={!!errors?.mirSmartMaxBlowsError}
              errorText={errors?.mirSmartMaxBlowsError}
            />
          </div>
          <div className='mir-smart-field-numeric'>
            <div className='device-type-subtitle'>Target Good Blows</div>
            <Input
              type='number'
              min={1}
              max={integratedDeviceFields.max_blows_allowed || 100}
              enforceMinMax
              onlyInteger
              isPositive
              disabled={preview}
              value={getSelectedKey('target_good_blows')}
              onChange={val => updateNumericField('target_good_blows', val)}
              hasError={!!errors?.mirSmartTargetBlowsError}
              errorText={errors?.mirSmartTargetBlowsError}
            />
          </div>
        </div>
      )}
      {isAccuChek && (
        <div className='accu-chek-question-text-section'>
          {renderQuestionTextSection({
            toggleField: 'has_on_demand_question_text',
            textField: 'on_demand_question_text',
            toggleLabel: STRINGS.integratedDeviceOnDemandQuestionTextToggle,
            placeholder: STRINGS.integratedDeviceOnDemandQuestionTextPlaceholder,
            isEnabled: hasOnDemandQuestionText,
            errorKey: 'blankOnDemandQuestionTextErr',
          })}
          {renderQuestionTextSection({
            toggleField: 'has_prefilled_question_text',
            textField: 'prefilled_question_text',
            toggleLabel: STRINGS.integratedDevicePrefilledQuestionTextToggle,
            placeholder: STRINGS.integratedDevicePrefilledQuestionTextPlaceholder,
            isEnabled: hasPrefilledQuestionText,
            errorKey: 'blankPrefilledQuestionTextErr',
          })}
        </div>
      )}
    </div>
  )
}

export default connect(null, itemActions)(IntegratedDevice)
